import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicPlan } from '../../../../lib/api/epics';
import { StoryDetail } from './StoryDetail';
import type { EpicDetail, StorySpec } from '../../../../../../shared/types/epics';
import { Card, CardHeader, CardBody, StatusBadge } from '../../../../components/ui';
import { epicSpend, formatCost, usdSp, cpsColor } from '../format';

// ---------------------------------------------------------------------------
// FORGE-055 — Story-level cost breakdown (FR-5)
// ---------------------------------------------------------------------------

export const StoryBreakdownTable: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  // The planner's per-story specs, fetched only once a row is expanded (shares
  // its cache entry with the Progress tab's story cards).
  const planQuery = useQuery({
    queryKey: ['epic-plan', String(epic.id)],
    queryFn: () => fetchEpicPlan(String(epic.id)),
    enabled: openIds.size > 0,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const specByIdx = useMemo(() => {
    const m = new Map<number, StorySpec>();
    for (const sp of planQuery.data?.stories ?? []) m.set(sp.idx, sp);
    return m;
  }, [planQuery.data]);

  // Only meaningful for real epics with at least one story carrying SP or cost.
  if (epic.kind === 'bugfix') return null;
  const rows = epic.stories.filter((s) => s.storyPoints !== null || s.lifetimeCostUsd > 0);
  if (rows.length === 0) return null;

  const spend = epicSpend(epic);
  const spTotal = epic.stories.reduce((n, s) => n + (s.storyPoints ?? 0), 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm font-semibold text-slate-900">By story — cost &amp; complexity</div>
          <div className="text-xs text-slate-500">
            Total spent{' '}
            <span className="font-mono font-semibold tabular-nums text-slate-900" data-testid="epic-total-spend">
              {formatCost(spend.total)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <table className="w-full text-sm" data-testid="story-breakdown-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Story</th>
              <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Title</th>
              <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold text-right">SP</th>
              <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold text-right">Cost</th>
              <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold text-right">Cost / SP</th>
              <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold text-right">Retries</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <React.Fragment key={s.id}>
              <tr className="border-b border-slate-100 last:border-b-0">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600">
                  <button
                    type="button"
                    onClick={() => toggle(s.id)}
                    aria-expanded={openIds.has(s.id)}
                    aria-label={`${openIds.has(s.id) ? 'Collapse' : 'Expand'} story ${s.id}`}
                    data-testid={`story-toggle-${s.id}`}
                    className="inline-flex items-center gap-1.5 hover:text-slate-900"
                  >
                    <span className="text-slate-400" aria-hidden>{openIds.has(s.id) ? '▾' : '▸'}</span>
                    {s.id}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-slate-900 truncate max-w-xs">
                  {s.title || <span className="italic text-slate-400">(untitled)</span>}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {s.storyPoints !== null ? s.storyPoints : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCost(s.lifetimeCostUsd)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-medium"
                  style={s.costPerStoryPoint !== null ? { color: cpsColor(s.costPerStoryPoint) } : undefined}>
                  {s.costPerStoryPoint !== null ? usdSp(s.costPerStoryPoint) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {s.retries > 0 ? (
                    <StatusBadge tone="warning">{s.retries}</StatusBadge>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>
              </tr>
              {openIds.has(s.id) && (
                <tr className="border-b border-slate-100">
                  <td colSpan={6} className="p-0">
                    <StoryDetail
                      epic={epic}
                      story={s}
                      spec={s.numericId !== null ? specByIdx.get(s.numericId) : undefined}
                      planLoading={planQuery.isLoading}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200">
            <tr className="font-semibold">
              <td className="px-4 py-2.5 text-slate-900" colSpan={2}>Epic total spent</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                {spTotal > 0 ? spTotal : <span className="text-slate-400">—</span>}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{formatCost(spend.total)}</td>
              <td /><td />
            </tr>
            {spend.setup > 0 && (
              <tr>
                <td className="px-4 pb-2.5 pt-0 text-right text-2xs text-slate-400" colSpan={6}>
                  includes {formatCost(spend.setup)} in setup runs (planner, graph) not tied to a story
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </CardBody>
    </Card>
  );
};
